import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import MaterialTable from "../../components/Table/MaterialTable";
import ActivityMappingForm from "./ActivityMappingForm";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const HotelActivityMapping = () => {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const handleExportToExcel = () => {
    const formattedData = mappings.map((row) => ({
      ID: row.id,
      Hotel:
        hotels.find((h) => h.id === row.hotel_id)?.hotel_name ||
        `ID: ${row.hotel_id}`,
      "Room Type":
        roomTypes.find((r) => r.id === row.room_type_id)?.room_type_name ||
        `ID: ${row.room_type_id}`,
      Activity:
        activities.find((a) => a.id === row.activity_id)?.activity_name ||
        `ID: ${row.activity_id}`,
      Status:
        row.status === "1" || row.status === "active" ? "Active" : "Inactive",
      "Created At": row.created_at
        ? new Date(row.created_at).toLocaleString()
        : "-",
      "Updated At": row.updated_at
        ? new Date(row.updated_at).toLocaleString()
        : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "ActivityMappings");
    XLSX.writeFile(workbook, "HotelActivityMappings.xlsx");
  };

  const fetchMappings = async () => {
    try {
      setLoading(true);
      const res = await fetch("https://api.esthell.com/hm_map_activities");
      const data = await res.json();
      if (data.success) {
        setMappings(data.data);
      } else {
        throw new Error("Invalid data format");
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setNotification({
        open: true,
        message: "Failed to fetch mappings",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [hotelsRes, roomsRes, activitiesRes] = await Promise.all([
        fetch("/api/hotels"),
        fetch("/api/room_types"),
        fetch("/api/activities"),
      ]);
      const [hotelsData, roomsData, activitiesData] = await Promise.all([
        hotelsRes.json(),
        roomsRes.json(),
        activitiesRes.json(),
      ]);

      setHotels(hotelsData.data || []);
      setRoomTypes(roomsData.data || []);
      setActivities(activitiesData.data || []);
    } catch (error) {
      console.error("Dropdown fetch error:", error);
    }
  };

  useEffect(() => {
    fetchMappings();
    fetchDropdowns();
  }, []);

  const columns = [
    { title: "ID", field: "id" },
    {
      title: "Hotel",
      field: "hotel_id",
      render: (row) =>
        hotels.find((h) => h.id === row.hotel_id)?.hotel_name ||
        `ID: ${row.hotel_id}`,
    },
    {
      title: "Room Type",
      field: "room_type_id",
      render: (row) =>
        roomTypes.find((r) => r.id === row.room_type_id)?.room_type_name ||
        `ID: ${row.room_type_id}`,
    },
    {
      title: "Activity",
      field: "activity_id",
      render: (row) =>
        activities.find((a) => a.id === row.activity_id)?.activity_name ||
        `ID: ${row.activity_id}`,
    },
    {
      title: "Status",
      field: "status",
      render: (row) => {
        const isActive = row.status === "1" || row.status === "active";
        return (
          <span
            style={{
              backgroundColor: isActive ? "#e6f4ea" : "#fce8e6",
              color: isActive ? "#1e4620" : "#c5221f",
              padding: "4px 8px",
              borderRadius: "4px",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      title: "Created",
      field: "created_at",
      render: (row) =>
        row.created_at ? new Date(row.created_at).toLocaleString() : "-",
    },
    {
      title: "Updated",
      field: "updated_at",
      render: (row) =>
        row.updated_at ? new Date(row.updated_at).toLocaleString() : "-",
    },
  ];

  const handleFormSubmit = async (formData) => {
    try {
      const isEdit = !!formData.id;
      const method = isEdit ? "PUT" : "POST";
      const url = isEdit
        ? `https://api.esthell.com/hm_map_activities/${formData.id}`
        : `https://api.esthell.com/create_map_activities`;

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: formData }),
      });

      if (!res.ok) throw new Error("Submission failed");

      setNotification({
        open: true,
        message: isEdit ? "Mapping updated!" : "Mapping created!",
        type: "success",
      });

      setShowForm(false);
      setEditData(null);
      fetchMappings();
    } catch (err) {
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Submit error:", err);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete mapping ID ${row.id}?`)) return;

    try {
      const res = await fetch(
        `https://api.esthell.com/hm_map_activities/${row.id}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );

      if (!res.ok) throw new Error("Failed to delete");

      setNotification({
        open: true,
        message: "Deleted successfully",
        type: "success",
      });
      fetchMappings();
    } catch (err) {
      setNotification({ open: true, message: err.message, type: "error" });
      console.error("Delete error:", err);
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Hotel Activity Mapping
        </Typography>
        <Button variant="outlined" onClick={handleExportToExcel}>
          Export to Excel
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ mt: "1cm", width: "100%" }}>
          <MaterialTable
            columns={columns}
            data={mappings}
            selection={false}
            actions={false}
            onEdit={(row) => {
              setEditData(row);
              setShowForm(true);
            }}
            onDelete={handleDelete}
            toolbar={{
              create: true,
              onCreateClick: () => {
                setEditData(null);
                setShowForm(true);
              },
            }}
            sx={{
              "& .MuiPaper-root": { width: "100%", overflow: "hidden" },
              "& .MuiTableContainer-root": {
                maxHeight: "calc(100vh - 250px)",
                overflowX: "auto",
                overflowY: "auto",
              },
            }}
          />
        </Box>
      )}

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", overflowY: "auto" } }}
      >
        <Box sx={{ p: 3 }}>
          <ActivityMappingForm
            hotels={hotels}
            roomTypes={roomTypes}
            activities={activities}
            initialData={editData}
            onClose={() => {
              setShowForm(false);
              setEditData(null);
            }}
            onSuccess={handleFormSubmit}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelActivityMapping;
